import { useCallback, useState } from 'react';
import { WhoAmIClue, WhoAmIPlayer } from '../types/games';

const defaultApiUrl = process.env.NODE_ENV === 'production'
  ? 'https://api.gibsonsleague.com/games/who-am-i/clues'
  : 'http://localhost:8787/games/who-am-i/clues';

const configuredAskUrl = process.env.REACT_APP_ASK_API_URL?.trim();
const whoAmIApiUrl = configuredAskUrl
  ? configuredAskUrl.replace(/\/ask\/?$/, '/games/who-am-i/clues')
  : defaultApiUrl;

interface ClueResponse {
  clues: WhoAmIClue[];
}

export const useWhoAmIClues = () => {
  const [isLoading, setIsLoading] = useState(false);

  const generateClues = useCallback(async (player: WhoAmIPlayer, hintCount: number, clueVariant: number) => {
    setIsLoading(true);
    try {
      const response = await fetch(whoAmIApiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          hintCount,
          clueVariant,
          player: {
            playerId: player.PlayerId,
            name: player.Name,
            position: player.Position,
            totalPoints: player.TotalPoints,
            gamesStarted: player.GamesStarted,
            pointsPerGame: player.PointsPerGame,
            seasons: player.Seasons,
            peakSeasonPointsPerGame: player.PeakSeasonPointsPerGame,
            peakSeasonYear: player.PeakSeasonYear,
            bestSeasonPositionRank: player.BestSeasonPositionRank ?? null,
            bestSeasonPositionRankYear: player.BestSeasonPositionRankYear ?? null,
            lastFantasySeason: player.LastFantasySeason,
            lastFantasySeasonWasKept: player.LastFantasySeasonWasKept,
            lastFantasySeasonDraftRound: player.LastFantasySeasonDraftRound ?? null,
            lastFantasySeasonFranchiseName: player.LastFantasySeasonFranchiseName ?? null,
            careerKeeperCount: player.CareerKeeperCount,
            careerDraftCount: player.CareerDraftCount,
            careerTradeCount: player.CareerTradeCount,
            careerDropCount: player.CareerDropCount,
            championships: player.Championships.map((championship) => ({
              year: championship.Year,
              franchiseId: championship.FranchiseId,
              franchiseName: championship.FranchiseName,
            })),
            franchises: player.Franchises.map((franchise) => ({
              id: franchise.Id,
              name: franchise.Name,
              gamesStarted: franchise.GamesStarted,
              pointsPerGame: franchise.PointsPerGame,
              seasons: franchise.Seasons,
            })),
          },
        }),
      });
      const payload = await response.json().catch(() => ({})) as ClueResponse & { error?: string };
      const expectedSources = [
        'nfl',
        'nfl',
        ...Array.from({ length: hintCount - 4 }, () => 'archive'),
        'nfl',
        'nfl',
      ].join(',');
      const validHints = Array.isArray(payload.clues)
        && payload.clues.length === hintCount
        && payload.clues.map((clue) => clue.source).join(',') === expectedSources
        && payload.clues.every((clue) => typeof clue.hint === 'string'
          && clue.hint.trim().length > 0
          && !clue.hint.includes('?'));
      if (!response.ok || !validHints) {
        throw new Error(payload.error || 'The Historian returned incomplete hints.');
      }
      return payload.clues;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { generateClues, isLoading };
};
